/* eslint-disable no-use-before-define */
/* eslint-disable no-underscore-dangle */
import { useDialog } from 'muibox';
import { useContext } from 'react';
import { InfiniteData, useInfiniteQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { handleHttpError } from '../utils/common';
import useAxios from './useAxios';
import { OnlyFansVaultMedias } from './useSubscriberVaultAlbums';

export enum ReleaseFormPartnerSource {
    Document = 'document',
    Tag = 'tag',
}

export enum ReleaseFormType {
    Document = 'document',
    User = 'user',
}

export enum ReleaseFormStatus {
    Approved = 'approved',
}

export interface ReleaseFormUser {
    view: ReleaseFormView;
    id: number;
    name: string;
    username: string;
    isVerified: boolean;
    avatar: null;
    avatarThumbs: null;
    ivStatus: null;
    isFromGuest: boolean;
}

export enum ReleaseFormView {
    Tu = 'tu',
}
export interface VaultMediaSettings {
    title: string;
    notes: string;
    description: string;
    updatedAt: Date;
}

export type VaultMediaResponseList = {
    id: number;
    canView: boolean;
    hasError: boolean;
    createdAt: Date;
    thumb: string;
    cachedThumb: string | null;
    src?: string;
    duration?: number;
    type: 'audio' | 'photo' | 'video' | 'gif';
    counters: {
        likesCount: number;
        tipsSumm: number;
    };
    mentionedUsers?: {
        view: string;
        avatar: string;
        avatarThumbs: {
            c50: string;
            c144: string;
        };
        header: string;
        headerSize: { width: number; height: number };
        headerThumbs: { w480: string; w760: string };
        id: number;
        name: string;
        username: string;
        hasNotViewedStory: boolean;
        isVerified: boolean;
        hasStream: boolean;
        hasStories: boolean;
        isRestricted: boolean;
        hasPromotion: boolean;
        isFree: boolean;
    }[];
    releaseForms?: {
        id: number;
        name: string;
        partnerSource: ReleaseFormPartnerSource;
        type: ReleaseFormType;
        status: ReleaseFormStatus;
        code: null;
        signature: null;
        date: Date;
        approve_date: Date;
        user: ReleaseFormUser;
    }[];
    full?: {
        url: string;
        width: number;
        height: number;
        size: number;
    };
    settings?: VaultMediaSettings;
};

export type VaultMediaResponse = {
    list: VaultMediaResponseList[];
    hasMore: boolean;
    nextOffset: number | null;
};

const useSubscriberVaultMedias = (
    service: string,
    albumId: number,
    type: OnlyFansVaultMedias.MediaType | null,
    sortDate: Date | null,
    sortType: OnlyFansVaultMedias.SortType,
    sortOrder: OnlyFansVaultMedias.SortOrder,
    enabled: boolean = true,
    queryStr: string | null = null,
) => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const params = useParams();
    const axios = useAxios();
    const dialog = useDialog();
    const qc = useQueryClient();

    const fetchVaultMedias = async (
        albumId: number,
        type: OnlyFansVaultMedias.MediaType | null,
        sortDate: Date | null,
        sortType: OnlyFansVaultMedias.SortType,
        sortOrder: OnlyFansVaultMedias.SortOrder,
        offset: number,
        queryStr: string | null,
    ): Promise<VaultMediaResponse> => {
        const query: string = `${settingsContext.routes.onlyFans.base}${params.userId}/vaultMedias?${new URLSearchParams({
            offset: offset.toString(),
            ...(albumId && { albumId: albumId.toString() }),
            ...(type && { type }),
            ...(sortDate && { sortDate: sortDate.toISOString() }),
            sortType,
            sortOrder,
            ...(queryStr && { query: queryStr }),
        })}`;

        return axios
            .get(query)
            .then(response => response.data)
            .catch(error => {
                console.error(error);
                handleHttpError(error, dialog);
                return { list: [], hasMore: false, nextOffset: null };
            });
    };
    /**
     * updateVaultMediaSettings - Updates a specific vaultItems settings
     * @param mediaId
     * @param newSettings
     * @returns a boolean of true for success or false for no success
     */
    // const updateVaultMediaSettings = async (mediaId: number, newSettings: VaultMediaSettings): Promise<boolean> => {
    //     const query: string = `${settingsContext.routes.onlyFans.base}${subscriber!._id}/updateMediaSettings/${mediaId}`;
    //     return axios
    //         .post(query, newSettings)
    //         .then(response => response.data)
    //         .catch(error => {
    //             console.error(error);
    //             handleHttpError(error, dialog);
    //         });
    // };

    // Use only stable primitives in the key (subscriberId instead of object)
    const queryKey = [
        'vaultMedias',
        service,
        albumId,
        type,
        sortDate?.toISOString() ?? null,
        sortType,
        sortOrder,
        queryStr,
        params.userId,
    ] as const;

    const queryResult = useInfiniteQuery(
        queryKey,
        params => fetchVaultMedias(albumId, type, sortDate, sortType, sortOrder, (params.pageParam as number) || 0, queryStr),
        {
            getNextPageParam: lastPage => lastPage?.nextOffset,
            refetchOnWindowFocus: false,
            staleTime: Infinity,
            enabled: params.userId && !!userContext.jwtToken && !!settingsContext.apiKey && enabled ? true : false,
        },
    );

    /**
     * Patch a single media item by id with a shallow partial update.
     * Only the page containing the item and the item itself get new references.
     */
    const patchVaultMediaInCache = (id: number, patch: Partial<VaultMediaResponseList>) => {
        const curr = qc.getQueryData<InfiniteData<VaultMediaResponse>>(queryKey);
        if (!curr) return; // nothing cached yet

        let touched = false;
        const pages = curr.pages.map(page => {
            const idx = page.list.findIndex(m => m.id === id);
            if (idx === -1) return page;

            touched = true;
            const list = page.list.slice();
            list[idx] = { ...list[idx], ...patch };
            return { ...page, list };
        });

        if (!touched) return;
        qc.setQueryData<InfiniteData<VaultMediaResponse>>(queryKey, { ...curr, pages });
    };

    /**
     * Update a single media item by id using an updater function
     * (handy for nested fields like settings/title).
     */
    // const updateVaultMediaInCache = (id: number, updater: (curr: VaultMediaResponseList) => VaultMediaResponseList) => {
    //     const curr = qc.getQueryData<InfiniteData<VaultMediaResponse>>(queryKey);
    //     if (!curr) return; // nothing cached yet

    //     let touched = false;
    //     const pages = curr.pages.map(page => {
    //         const idx = page.list.findIndex(m => m.id === id);
    //         if (idx === -1) return page;

    //         touched = true;
    //         const list = page.list.slice();
    //         list[idx] = updater(list[idx]);
    //         return { ...page, list };
    //     });

    //     if (!touched) return;
    //     qc.setQueryData<InfiniteData<VaultMediaResponse>>(queryKey, { ...curr, pages });
    // };

    return {
        ...queryResult,
        patchVaultMediaInCache,
        // updateVaultMediaInCache,
        // updateVaultMediaSettings,
    };
};

export default useSubscriberVaultMedias;
